import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from 'constants/endpoints';
import { useMonitoringPortalContext } from 'hooks/useMonitoringPortalContext';
import { usePopup } from 'context/PopupProvider';
import {
  ArrowLeft,
  ShieldAlert,
  RefreshCw,
  X,
  AlertCircle,
  Gamepad2,
  Receipt,
  Copy,
  Check,
  Calendar,
  Swords,
  Trophy,
  ExternalLink,
  Eye,
} from 'lucide-react';
import {
  USER_STATUS,
  USER_STATUS_LABEL,
  BATTLE_STATUS_LABEL,
  BATTLE_TYPE_LABEL,
  TRANSACTION_STATUS_LABEL,
  labelFor,
  statusPillByLabel,
  transactionTypeLabel,
} from 'constants/enums';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_EYE_BTN_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';

const money = (symbol: string, v: any) =>
  `${symbol}${parseFloat(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

function CopyIdCell({ value, label }: { value?: string | null; label: string }) {
  const [copied, setCopied] = useState(false);
  const full = value?.trim() || '—';
  const canCopy = full !== '—';

  const copyId = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="flex items-start gap-1.5 min-w-[180px] max-w-[280px]">
      <span className="font-mono text-[11px] text-muted break-all leading-snug">{full}</span>
      {canCopy && (
        <button
          type="button"
          onClick={() => void copyId()}
          title={copied ? 'Copied' : `Copy ${label}`}
          aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
          className="shrink-0 p-1 rounded-md text-faint hover:text-heading hover:bg-subtle transition-colors"
        >
          {copied ? <Check size={14} strokeWidth={2.5} className="text-emerald-600" /> : <Copy size={14} strokeWidth={2} />}
        </button>
      )}
    </div>
  );
}

const battleTypePill = (code: number | string) =>
  Number(code) === 2
    ? 'bg-amber-500/10 text-amber-700 border-amber-200'
    : 'bg-subtle text-muted border-line';

const playerInitials = (username: string) =>
  username
    .replace(/[^a-zA-Z0-9]/g, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

const variantLabel = (row: { variant_name?: string; variant_code?: string }) => {
  const name = row.variant_name?.trim();
  const code = row.variant_code?.trim();
  if (name) return name;
  if (code) return code;
  return 'Unknown';
};

type StatCardProps = {
  icon: React.ElementType;
  label: string;
  value: string | number;
  sub?: string;
  iconWrap: string;
};

function StatCard({ icon: Icon, label, value, sub, iconWrap }: StatCardProps) {
  return (
    <div className="rounded-xl border border-line bg-card p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl mb-3 ${iconWrap}`}>
        <Icon size={20} strokeWidth={2} />
      </div>
      <p className="text-[11px] font-bold text-muted uppercase tracking-wider">{label}</p>
      <p className="text-2xl sm:text-3xl font-extrabold text-heading mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-muted mt-2 leading-snug">{sub}</p>}
    </div>
  );
}

/**
 * Full player detail page — profile, battle history, and transactions.
 */
const PlayerEnforcement = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { confirm } = usePopup();
  const portal = useMonitoringPortalContext();
  const { apiClient, isConsumer } = portal;

  const [details, setDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');

  const fetchDetails = useCallback(() => {
    if (!id) return;
    setLoading(true);
    apiClient.get(portal.endpoints.playerDetails(id))
      .then((res) => { if (res.success && res.data) setDetails(res.data); })
      .catch((err) => console.error('Failed to fetch player details:', err))
      .finally(() => setLoading(false));
  }, [id, apiClient, portal]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  const handleStatusUpdate = async () => {
    if (!id || !details) return;
    const p = details.player;
    const suspending = p.status === USER_STATUS.ACTIVE;
    if (suspending) {
      const confirmed = await confirm({
        title: 'Suspend Account',
        message: `Suspend ${p.username} (USR-${p.id})? They will be blocked from playing until reactivated.`,
        variant: 'danger',
        confirmLabel: 'Suspend',
        cancelLabel: 'Cancel',
      });
      if (!confirmed) return;
    }
    const newStatus = suspending ? USER_STATUS.SUSPENDED : USER_STATUS.ACTIVE;
    apiClient.post(API_ENDPOINTS.ADMIN.PLAYERS.UPDATE_STATUS(id), { status: newStatus })
      .then((res) => {
        if (res.success) {
          setNotice(`Account status changed to ${USER_STATUS_LABEL[newStatus]}.`);
          fetchDetails();
        }
      })
      .catch((err) => console.error('Failed to update status:', err));
  };

  if (loading && !details) {
    return (
      <div className="flex h-64 items-center justify-center text-muted font-medium">
        <RefreshCw className="animate-spin mr-2" size={20} /> Loading player details...
      </div>
    );
  }

  if (!details) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <button onClick={() => navigate(portal.backToPlayersPath)} className="inline-flex items-center gap-2 text-sm font-bold text-muted hover:text-heading">
          <ArrowLeft size={16} /> Back to Players
        </button>
        <div className="bg-card p-12 rounded-2xl border border-line text-center text-muted font-medium">
          Player USR-{id} not found or could not be loaded.
        </div>
      </div>
    );
  }

  const p = details.player;
  const battles: any[] = Array.isArray(details.battles) ? details.battles : [];
  const transactions: any[] = Array.isArray(details.transactions) ? details.transactions : [];
  const battlesByMode: any[] = Array.isArray(details.battlesByMode) ? details.battlesByMode : [];
  const activity = details.activitySummary ?? {};
  const totalBattles = Number(activity.totalBattles ?? 0);
  const completedBattles = Number(activity.completedBattles ?? 0);
  const transactionCount = Number(activity.transactionCount ?? transactions.length);

  return (
    <div className="space-y-6 animate-fadeIn">
      <button
        onClick={() => navigate(portal.backToPlayersPath)}
        className="inline-flex items-center gap-2 text-sm font-semibold text-muted hover:text-heading transition-colors group"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-card group-hover:bg-subtle transition-colors">
          <ArrowLeft size={15} />
        </span>
        Back to Players
      </button>

      {/* Profile */}
      <div className="rounded-2xl border border-line bg-card shadow-sm p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <div className="relative shrink-0">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-brand-dark text-brand-gold text-lg font-bold shadow-sm">
                {playerInitials(p.username)}
              </div>
              <span
                className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-card ${p.status === USER_STATUS.ACTIVE ? 'bg-emerald-500' : 'bg-red-500'}`}
                title={labelFor(USER_STATUS_LABEL, p.status)}
              />
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h1 className="text-xl sm:text-2xl font-bold text-heading tracking-tight">
                  {p.display_name || p.username}
                </h1>
                {p.external_user_id && (
                  <span className="text-sm font-mono font-medium text-muted">{p.external_user_id}</span>
                )}
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md uppercase tracking-wide ${p.status === USER_STATUS.ACTIVE ? 'bg-emerald-500/10 text-emerald-700' : 'bg-red-500/10 text-red-700'}`}>
                  {labelFor(USER_STATUS_LABEL, p.status)}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-sm text-muted">
                {p.username && (
                  <span>@{p.username}</span>
                )}
                {p.created_at && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar size={13} />
                    Joined {new Date(p.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {!isConsumer && (
              <button
                type="button"
                onClick={() => void handleStatusUpdate()}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${p.status === USER_STATUS.ACTIVE
                  ? 'border-red-200 bg-red-500/10 text-red-700 hover:bg-red-500/15'
                  : 'border-emerald-200 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/15'
                  }`}
              >
                <ShieldAlert size={15} />
                {p.status === USER_STATUS.ACTIVE ? 'Suspend' : 'Activate'}
              </button>
            )}
            <button
              type="button"
              onClick={fetchDetails}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-line text-muted hover:text-heading hover:bg-subtle transition-colors"
              title="Refresh"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* Activity KPI cards */}
      <div>
        <h2 className="text-sm font-bold text-heading mb-3">Activity overview</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {(battlesByMode.length > 0 ? battlesByMode : [{ variant_name: 'All modes', battle_count: 0, completed_count: 0 }]).map((row, idx) => {
            const completed = Number(row.completed_count || 0);
            const total = Number(row.battle_count || 0);
            return (
              <StatCard
                key={row.variant_id ?? row.variant_code ?? row.variant_name ?? idx}
                icon={Swords}
                label={variantLabel(row)}
                value={total.toLocaleString()}
                sub={completed > 0 ? `${completed.toLocaleString()} completed` : 'Total battles played'}
                iconWrap="bg-indigo-500/10 text-indigo-600"
              />
            );
          })}

          <StatCard
            icon={Trophy}
            label="Games completed"
            value={completedBattles.toLocaleString()}
            sub={totalBattles > 0 ? `${Math.round((completedBattles / totalBattles) * 100)}% of all battles` : 'Finished battles'}
            iconWrap="bg-emerald-500/10 text-emerald-600"
          />

          <StatCard
            icon={Receipt}
            label="Transactions"
            value={`${transactionCount.toLocaleString()}${transactionCount >= 50 ? '+' : ''}`}
            sub={transactionCount >= 50 ? 'Latest 50 shown in ledger below' : 'Wallet & in-game ledger entries'}
            iconWrap="bg-blue-500/10 text-blue-600"
          />

          {battlesByMode.length > 1 && (
            <StatCard
              icon={Gamepad2}
              label="Total battles"
              value={totalBattles.toLocaleString()}
              sub="Across all game modes"
              iconWrap="bg-brand-gold/15 text-brand-gold"
            />
          )}
        </div>
      </div>

      {notice && (
        <div className="bg-amber-500/10 text-amber-800 p-4 rounded-xl border border-amber-200 text-sm font-semibold flex items-center gap-2">
          <AlertCircle size={18} /> {notice}
          <button onClick={() => setNotice('')} className="ml-auto text-amber-500 hover:text-amber-700"><X size={16} /></button>
        </div>
      )}

      {/* Battles */}
      <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
        <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between gap-3`}>
          <div className="flex items-center gap-2">
            <Gamepad2 size={18} className="text-indigo-600" />
            <div>
              <h4 className="font-bold text-heading">Battle History</h4>
              <p className="text-xs text-muted">Latest 50 battles · tables this player has joined</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(portal.battlesListWithUserFilter(p.id, p.username))}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-line bg-subtle/60 text-xs font-bold text-heading hover:bg-subtle transition-colors shrink-0"
          >
            View all <ExternalLink size={13} />
          </button>
        </div>
        <div className="overflow-auto max-h-[50vh]">
          <table className={LIST_TABLE_CLASS}>
            <thead className={LIST_THEAD_CLASS}>
              <tr>
                <th className={`${LIST_TH_NO_CLASS}`}>No.</th>
                {['Battle', 'Type', 'Mode', 'Table', 'Join / Leave', 'Rounds', 'Pot', 'Status', ''].map((h, i) => (
                  <th key={i} className={LIST_TH_CLASS}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className={`${LIST_TBODY_CLASS} text-sm`}>
              {battles.length > 0 ? battles.map((b, index) => (
                <tr key={b.id} className="hover:bg-subtle/50 transition-colors">
                  <td className={LIST_TD_NO_CLASS}>{listRowNumber(1, index, 0)}</td>
                  <td className={`${LIST_TD_CLASS}`}>
                    <div className="font-bold text-heading">BTL-{b.id}</div>
                    <CopyIdCell value={b.uuid} label="battle ID" />
                  </td>
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                    <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full border ${battleTypePill(b.battle_type)}`}>
                      {labelFor(BATTLE_TYPE_LABEL, b.battle_type)}
                    </span>
                  </td>
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap text-body font-medium`}>
                    {b.variant_name || b.variant_code || '—'}
                  </td>
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap text-body`}>
                    <div>{b.table_name || '—'}</div>
                    {b.room_code && <div className="text-[10px] font-mono text-muted">{b.room_code}</div>}
                  </td>
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap text-body`}>
                    <div>{money(b.currency_symbol || '', b.join_amount)}</div>
                    {b.leave_amount != null && (
                      <div className="text-xs text-muted">Out {money(b.currency_symbol || '', b.leave_amount)}</div>
                    )}
                  </td>
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>{b.total_rounds ?? '—'}</td>
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap font-bold text-heading`}>{money(b.currency_symbol || '', b.pot_amount)}</td>
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                    <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full border ${statusPillByLabel(labelFor(BATTLE_STATUS_LABEL, b.status))}`}>
                      {labelFor(BATTLE_STATUS_LABEL, b.status)}
                    </span>
                  </td>
                  <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                    <button
                      type="button"
                      onClick={() => navigate(portal.battleDetailPath(b.id))}
                      className={LIST_EYE_BTN_CLASS}
                      title="View battle"
                      aria-label="View battle"
                    >
                      <Eye size={16} strokeWidth={2} />
                    </button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={10} className="px-5 py-10 text-center text-muted font-medium">
                    No battles recorded for this player.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transactions */}
      <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
        <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between gap-3`}>
          <div className="flex items-center gap-2">
            <Receipt size={18} className="text-brand-gold" />
            <div>
              <h4 className="font-bold text-heading">Transactions</h4>
              <p className="text-xs text-muted">Latest 50 transactions · in-game and wallet ledger entries</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate(portal.transactionsListWithUserFilter(p.id, p.username))}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-line bg-subtle/60 text-xs font-bold text-heading hover:bg-subtle transition-colors shrink-0"
          >
            View all <ExternalLink size={13} />
          </button>
        </div>
        <div className="overflow-auto max-h-[50vh]">
          <table className={LIST_TABLE_CLASS}>
            <thead className={LIST_THEAD_CLASS}>
              <tr>
                <th className={`${LIST_TH_NO_CLASS}`}>No.</th>
                {['Transaction ID', 'Type', 'Battle', 'Amount', 'Balance', 'Status', 'Date'].map((h, i) => (
                  <th key={i} className={LIST_TH_CLASS}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className={`${LIST_TBODY_CLASS} text-sm`}>
              {transactions.length > 0 ? transactions.map((t, index) => {
                const sym = t.currency_symbol || '';
                const amt = parseFloat(t.amount);
                const isCredit = amt >= 0;
                return (
                  <tr key={t.id} className="hover:bg-subtle/50 transition-colors">
                    <td className={LIST_TD_NO_CLASS}>{listRowNumber(1, index, 0)}</td>
                    <td className={`${LIST_TD_CLASS}`}>
                      <CopyIdCell value={t.transaction_uuid} label="transaction ID" />
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-subtle text-body">
                        {transactionTypeLabel(t.transaction_type, t.action_code)}
                      </span>
                      {t.table_name && <div className="text-[10px] text-muted mt-0.5">{t.table_name}</div>}
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-body`}>
                      {t.battle_id ? (
                        <button
                          onClick={() => navigate(portal.battleDetailPath(t.battle_id))}
                          className="font-semibold text-indigo-600 hover:underline"
                        >
                          BTL-{t.battle_id}
                        </button>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                      {t.round_number != null && (
                        <div className="text-[10px] text-muted">Round {t.round_number}</div>
                      )}
                    </td>
                    <td className={`px-5 py-3 whitespace-nowrap font-bold ${isCredit ? 'text-green-600' : 'text-red-500'}`}>
                      {isCredit ? '+' : '-'}{money(sym, Math.abs(amt))}
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-muted text-xs`}>
                      {t.balance_before != null && t.balance_after != null ? (
                        <span>{money(sym, t.balance_before)} → {money(sym, t.balance_after)}</span>
                      ) : '—'}
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-subtle text-body">
                        {labelFor(TRANSACTION_STATUS_LABEL, t.status)}
                      </span>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-muted text-xs`}>
                      {new Date(t.created_at).toLocaleString()}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={8} className="px-5 py-10 text-center text-muted font-medium">
                    No transactions recorded for this player.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlayerEnforcement;
